import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import "./styles.css";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import { urlFatura } from "../../../../../components/cte/FaturaDoCte";
import { SITUACOES_FATURA } from "../../../ConferenciaFatura/formatters";

// Rótulo do HUB (o mesmo da Auditoria de Fatura): o Nome do TMS para a 8 é
// "Paga", mas no HUB ela é "Integrada".
export const rotuloSituacao = (idSituacao, fallback) => {
  const situacao = SITUACOES_FATURA.find(
    (s) => s.value === Number(idSituacao)
  );
  return situacao ? situacao.label : fallback || null;
};

// Formata pela parte da data (AAAA-MM-DD). Cobre o ISO da Fatura
// ("2026-10-01T00:00:00.000Z", meia-noite UTC) e o texto com fuso da
// Work_Fatura ("2026-11-04 00:00:00.000 -03:00"), que o moment não reconhece.
const formatarData = (valor) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(valor || ""));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "-";
};

/**
 * Detalhe de uma fatura, compartilhado pelo calendário "Faturas por
 * Vencimento" e pelo kanban "Status das Faturas".
 *
 * fatura: {
 *   numFatura, valor, transportadora, situacao (rótulo), vencimento, emissao,
 *   qtdCtes, idFatura (sem ele, não há botão para a Auditoria),
 *   prazo: { label, cor } (opcional), titulo (fallback sem numFatura)
 * }
 */
const FaturaDetalheModal = ({ fatura, onClose }) => {
  useEffect(() => {
    if (!fatura) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [fatura, onClose]);

  if (!fatura || typeof document === "undefined") {
    return null;
  }

  const temDetalhes = Boolean(fatura.numFatura);
  const linhas = [
    ["Transportadora", fatura.transportadora || "-"],
    ["Situação", fatura.situacao || "-"],
    ["Vencimento", formatarData(fatura.vencimento)],
    ["Emissão", formatarData(fatura.emissao)],
    ["CT-es", fatura.qtdCtes ?? "-"],
  ];
  const temValor = fatura.valor !== null && fatura.valor !== undefined;

  const modalContent = (
    <div className="fatura-detalhe-backdrop" onClick={onClose}>
      <div
        className="fatura-detalhe-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="fatura-detalhe-header">
          <h5>{temDetalhes ? `Fatura ${fatura.numFatura}` : "Fatura"}</h5>
          <button
            type="button"
            className="fatura-detalhe-close"
            aria-label="Fechar"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="fatura-detalhe-body">
          {temDetalhes ? (
            <>
              <div className="fatura-detalhe-topo">
                <span className="fatura-detalhe-valor">
                  {temValor ? formatCurrency(Number(fatura.valor) || 0) : "-"}
                </span>
                {fatura.prazo && (
                  <span
                    className="fatura-detalhe-prazo"
                    style={{ "--prazo-cor": fatura.prazo.cor }}
                  >
                    {fatura.prazo.label}
                  </span>
                )}
              </div>
              <dl className="fatura-detalhe-lista">
                {linhas.map(([rotulo, valor]) => (
                  <React.Fragment key={rotulo}>
                    <dt>{rotulo}</dt>
                    <dd>{valor}</dd>
                  </React.Fragment>
                ))}
              </dl>
              {fatura.idFatura ? (
                <Link
                  className="btn btn-primary btn-block fatura-detalhe-abrir"
                  to={urlFatura({ id: fatura.idFatura })}
                  onClick={onClose}
                >
                  Abrir na Auditoria de Fatura
                </Link>
              ) : (
                <p className="fatura-detalhe-aviso">
                  Fatura ainda não importada no sistema: aguardando os CT-es
                  para entrar na Auditoria.
                </p>
              )}
            </>
          ) : (
            <p style={{ marginBottom: 0 }}>{fatura.titulo}</p>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FaturaDetalheModal;
